// What a power-up is worth to a bot: one table, global, for every bot that has
// any Power-up greed at all. The values here are the DEFAULTS; an admin can
// retune them from Bot management, and a match then carries its own table
// (matchConfig / gameState `powerUpGreedValues`), which wins over these.
//
// 0..1 per power-up (keyed like core constants' powerUpDefinitions):
//   0  the bots ignore it completely, even at greed 1 (and a bot with negative
//      greed doesn't bother avoiding it either);
//   1  full pull: a bot with greed 1 treats it exactly as it did before this
//      table existed.
// The value multiplies the planner's existing pull (path-checker-ai-handler.js:
// greed, who the effect targets, a discount when a rival will get there first),
// so a table of all 1s is the old behaviour. A bot's OWN greed trait says how
// much it cares about power-ups; this table says which ones are worth caring
// about. Hand-tuned for now; a candidate for measuring later.
import * as constants from "../core/constants.js";

export var POWER_UP_GREED_VALUES = {
    // green: helps the one who takes it
    slim: 1,             // thinner = fits gaps nobody else can: the best thing on the board
    quick_turn: 0.9,     // tighter turns = more escapes
    super_jump: 0.7,     // hops a trail once in a while; good, not vital
    twin: 0.6,           // a second worm scores twice and dies twice
    slow: 0.5,           // slower = more time to react
    wall_hack_self: 0.6, // (not spawning at the moment)
    // red: hurts everyone else
    fat: 1,              // fat rivals run out of room fast
    slow_turn: 1,        // rivals that can't turn crash
    drunk: 0.9,
    key_switch: 0.9,     // brutal on people
    tron_turn: 0.8,
    speed: 0.6,          // fast rivals crash more
    // blue: hits everybody, the taker included
    wall_hack_all: 0.6,
    clear_all: 0.7,      // (not spawning at the moment)
    switcharoonie: 0,    // a coin flip on who ends up where: never worth a detour. IGNORED.
};

// The board model knows a power-up by its definition's `name` and who it
// `affects` (two definitions can share a name: wall_hack for self and for all),
// so resolve the table's key through the definitions once.
var KEY_BY_NAME_AND_AFFECTS = {};
Object.keys(constants.powerUpDefinitions).forEach(function (key) {
    var def = constants.powerUpDefinitions[key];
    KEY_BY_NAME_AND_AFFECTS[def.name + "|" + def.affects] = key;
});

export function powerUpKey(powerUp) {
    if (!powerUp) { return null; }
    return KEY_BY_NAME_AND_AFFECTS[powerUp.name + "|" + powerUp.affects] || null;
}

// A power-up's value, 0..1: the match's own table when it has one for this
// power-up, else the default. One neither knows (a new power-up nobody has
// valued yet) counts in full, which is what it would have done before.
export function powerUpGreedValue(powerUp, matchValues) {
    var key = powerUpKey(powerUp);
    var v = key !== null && matchValues && typeof matchValues[key] === "number" ? matchValues[key]
        : key !== null ? POWER_UP_GREED_VALUES[key] : undefined;
    if (typeof v !== "number" || !isFinite(v)) { return 1; }
    return Math.min(1, Math.max(0, v));
}

// A saved table, made safe: known power-ups only, numbers only, clamped to
// 0..1 and rounded to 2 decimals. Returns null when nothing valid is left.
export function cleanPowerUpValues(input) {
    if (!input || typeof input !== "object") { return null; }
    var out = {};
    var any = false;
    Object.keys(POWER_UP_GREED_VALUES).forEach(function (key) {
        var v = input[key];
        if (typeof v !== "number" || !isFinite(v)) { return; }
        out[key] = Math.round(Math.min(1, Math.max(0, v)) * 100) / 100;
        any = true;
    });
    return any ? out : null;
}
